// js/core/production-crew.js — "Whose production is this?", as three states.
//
// An admin filing a Production Report says whose production it is: the
// company's own crew (costed by the hour, through timesheets) or a subcontract
// crew (costed per unit, off production_reports.foreman_id). The picker used
// to open on "Own crew", so a sub's week typed by the office and never changed
// filed as own crew: units that earned and consumed material at a labour cost
// of zero. Owner's decision, 2026-10-02: there is NO silent default — the
// admin has to pick.
//
// "Own crew" and "a crew" used to be the only two states (crew id null / set),
// which left no way to tell "own crew, chosen" from "not chosen yet". This is
// the third state, and the ONE place the rules about it live:
//
//   { picked: false, id: null }   not answered yet     → the form will not file
//   { picked: true,  id: null }   own crew, on purpose
//   { picked: true,  id: <uuid> } that subcontract crew
//
// What the pick moves is unchanged (docs/submit-identity.md): only
// production_reports.foreman_id. The author, the PDF and the signature stay
// with the account that filed it.
//
// Pure: no DOM, no storage — js/app.js hands everything in.

/** The <select> value that means "our own crew". Never a profile id. */
export const OWN = 'own';

const NONE = Object.freeze({ picked: false, id: null, name: '' });

/**
 * The choice a saved draft carries (drafts.form_data.__crew), for an admin.
 *
 *   absent                 a draft from before the question was asked, or one
 *                          where it was not answered → not picked: it asks once
 *   { id: null }           own crew, chosen
 *   { id, name }           that crew — also the only shape older drafts wrote
 *
 * A non-admin's draft never carries one, and one that somehow does is ignored:
 * the database refuses a foreman writing another crew's production.
 */
export function crewFromDraft(saved, isAdmin) {
  if (!isAdmin || !saved || typeof saved !== 'object') return { ...NONE };
  const id = saved.id || null;
  return { picked: true, id, name: id ? (saved.name || '') : '' };
}

/** What to store in the draft: nothing until the question has been answered. */
export function crewToDraft(choice) {
  if (!choice || !choice.picked) return undefined;
  const id = choice.id || null;
  return { id, name: id ? (choice.name || '') : '' };
}

/** A <select> value → the choice. '' is "nothing chosen". */
export function crewFromSelect(value, name) {
  if (!value) return { ...NONE };
  if (value === OWN) return { picked: true, id: null, name: '' };
  return { picked: true, id: value, name: name || '' };
}

/** The choice → the <select> value that shows it. */
export function crewSelectValue(choice) {
  if (!choice || !choice.picked) return '';
  return choice.id || OWN;
}

/**
 * The subcontract crews this account can pick: every flagged profile but its
 * own ("own crew" already means exactly that).
 */
export function pickableCrews(crews, myId) {
  return (crews || []).filter(p => p && p.id && p.id !== myId);
}

/**
 * Must the form stop and ask? Only an ADMIN, only on a Production Report, only
 * in an org that has a subcontract crew to choose — and only while the
 * question is unanswered. A foreman filing his own report is never asked, and
 * neither is an org with no sub crews: there is nothing to choose between.
 */
export function crewChoiceMissing({ role, formType, crews, myId, picked }) {
  if (role !== 'admin' || formType !== 'Production Report') return false;
  return pickableCrews(crews, myId).length > 0 && !picked;
}
